import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query'
import { getInboxPage, getLoanThreads } from 'services/messages'
import { LOAN_THREADS_KEY, UNSEEN_COUNT_KEY, inboxKey } from 'modules/inbox'

/**
 * The member's messages, newest first, narrowed by time and by type.
 *
 * Each filter is its own cache entry, so flipping back to one already seen is
 * instant. The first page carries the unseen count, which keeps the footer
 * badge honest without a second request.
 *
 * The count is copied when the first page *arrives*, not in `onSuccess`: in
 * react-query 3 that runs again on every next page and on every cache edit,
 * and each time it wrote the count from the moment the page opened back over
 * the badge — which is why it kept coming back after the messages were read.
 */
export default function useInbox(
    period: TInboxPeriod | undefined,
    category: TInboxCategory | undefined
) {
    const queryClient = useQueryClient()
    return useInfiniteQuery(
        inboxKey(period, category),
        async ({ pageParam }) => {
            const page = await getInboxPage({
                before: pageParam,
                period,
                category,
            })
            if (!pageParam && typeof page.unseen_count === 'number')
                queryClient.setQueryData(UNSEEN_COUNT_KEY, page.unseen_count)
            return page
        },
        {
            getNextPageParam: (last) => last.next ?? undefined,
            // A keyset page is one indexed range scan, so unlike the old
            // day-paged endpoint a refetch on focus is cheap — and it is what
            // turns a message read on the phone green here.
            staleTime: 15_000,
            keepPreviousData: true,
        }
    )
}

export function useLoanThreads(enabled = true) {
    return useQuery(LOAN_THREADS_KEY, getLoanThreads, {
        enabled,
        staleTime: 30_000,
    })
}
